import _ from 'underscore';
import {parseString} from 'xml2js';
import jsonTransform from '../jsonTransform/jsonTransformers';
import {ReportingDocumentType, FeatureFlagEnum} from '../enums';
import reportingDocumentErrorTemplate from './templates/errorDetailTemplate.json'
import reportingDocumentErrorIntrastatTemplate from './templates/errorDetailsIntrastatTemplate.json'
import reportingDocumentErrorIntrastatTemplateV2 from './templates/errorDetailsIntrastatTemplateV2.json'
import reportingDocumentSAPTypeRVTemplate from './templates/errorDetailSAPDocumentTemplate.json'
import Logger, {LOG_LEVELS} from '../logger';
import jsonpath from '../jsonpath';

const log = new Logger('ERROR_DETAILS_UTIL', LOG_LEVELS.DEBUG);

const commonJSONTransformer = function(original={}, sourceJSON={}, reportingDocumentType=ReportingDocumentType.TYPE_1, templateJSON=reportingDocumentErrorTemplate) {
    log.debug('commonJSONTransformer(): ', sourceJSON);
    let json = original;
    json.errorJson = sourceJSON;

  if (reportingDocumentType === ReportingDocumentType.INTRASTAT_DOCUMENT_TYPE) {
    const intrastatReturn = sourceJSON.ReportingDocument.IntrastatReturn;
    
    const originalDescription = sourceJSON.ReportingDocument.InvoiceProperties.InvoiceLines?.Description ||
      (sourceJSON.ReportingDocument.InvoiceProperties.InvoiceLines &&
        sourceJSON.ReportingDocument.InvoiceProperties.InvoiceLines[0]?.Description);

    const originalInvoiceLines = json.errorJson.ReportingDocument.InvoiceProperties.InvoiceLines;
    json.errorJson.ReportingDocument.InvoiceProperties.InvoiceLines = _.isArray(originalInvoiceLines) ?
      originalInvoiceLines :
      [originalInvoiceLines || { Description: originalDescription }];

    if (json.errorJson.ReportingDocument.InvoiceProperties.InvoiceLines.length === 0) {
      json.errorJson.ReportingDocument.InvoiceProperties.InvoiceLines.push({ Description: originalDescription });
    }

    if (intrastatReturn) {
      if (_.isArray(intrastatReturn)) {
        const newInvoiceLines = [];
        intrastatReturn.forEach(returnEntry => {
          if (returnEntry.IntrastatDetails) {
            newInvoiceLines.push({
              IntrastatData: returnEntry.IntrastatDetails,
              Description: originalDescription
            });
          }
        });
        if (newInvoiceLines.length > 0) {
          json.errorJson.ReportingDocument.InvoiceProperties.InvoiceLines = newInvoiceLines;
          json.errorJson.ReportingDocument.IntrastatReturn = intrastatReturn[0];
        }
      } else if (_.isObject(intrastatReturn) && intrastatReturn.IntrastatDetails) {
        json.errorJson.ReportingDocument.InvoiceProperties.InvoiceLines[0] = {
          ...json.errorJson.ReportingDocument.InvoiceProperties.InvoiceLines[0],
          IntrastatData: intrastatReturn.IntrastatDetails,
          Description: originalDescription
        };
        json.errorJson.ReportingDocument.IntrastatReturn = intrastatReturn;
      }
    }

    if (sourceJSON.ReportingDocument.InvoiceProperties && sourceJSON.ReportingDocument.InvoiceProperties.IntrastatDocumentData) {
      json.errorJson.ReportingDocument.InvoiceProperties.IntrastatDocumentData = sourceJSON.ReportingDocument.InvoiceProperties.IntrastatDocumentData;
    }
  }

  // Make the InvoiceLines an array when it isn't so we can use the .map to show repeating lines
  if (!_.isArray(json.errorJson.ReportingDocument.InvoiceProperties.InvoiceLines)) {
    json.errorJson.ReportingDocument.InvoiceProperties.InvoiceLines = [json.errorJson.ReportingDocument.InvoiceProperties.InvoiceLines];
  }

    // Make the data structure consistent against the template
    json.errorJson = jsonTransform.doRestructureWithTemplate(templateJSON, json.errorJson);
    // Make a new element in the datastructure to maintain the Structured version and poke in the flat reference
    json.flatFieldMap = jsonTransform.doObjectMapToFlatReference(json.errorJson, 'errorJson');
    // Push the currencies for this document into state too
    let currencies = [];
    if (Array.isArray(json.errorJson.ReportingDocument.InvoiceProperties.InvoiceValues)) {
        json.errorJson.ReportingDocument.InvoiceProperties.InvoiceValues.forEach(
            (invoiceValue) => {
                currencies.push(invoiceValue.Currency);
            }
        )
    }
    json.reportingDocumentType = reportingDocumentType;
    json.currencies = currencies;
    return json;
}

//Select all AdditionalDocumentReference with SAPDocumentType
const SAP_DOCUMENT_TYPE_RV_JSON_PATH = '$.ReportingDocument.InvoiceProperties.AdditionalDocumentReference[?(@.SAPDocumentType)]';
const SAP_INTRASTAT_INTRASTAT_RETURN_PATH = '$.ReportingDocument.IntrastatReturn'
const CONNECTOR_NAME_PATH = '$.ReportingDocument.ConnectorName';
const MANUAL_CORRECTION_INVOICE_NUMBER_PATH = '$.ReportingDocument.InvoiceProperties.InvoiceNumber';

class ErrorDocumentUtil {

  constructor(errorData={}) {
    this.errorData = errorData;
    this.errorDataJson = {};
    this.parseCallback = this.parseCallback.bind(this);
    parseString(errorData.xml, {explicitArray: false}, this.parseCallback);
  }

  parseCallback(err, result) {
    this.errorDataJson = result;
  }

  getDocumentType() {

    let connectorName = jsonpath.query(this.errorDataJson, CONNECTOR_NAME_PATH);
    connectorName = connectorName[0];

    if (!_.isEmpty(jsonpath.query(this.errorDataJson, SAP_INTRASTAT_INTRASTAT_RETURN_PATH))) {
      log.debug("getDocumentType: Found Intrastat document based on IntrastatReturn tag.");
      return ReportingDocumentType.INTRASTAT_DOCUMENT_TYPE;
    }

    switch (connectorName) {
      case "Avalara_Generic2Reporting":
      case "Avalara_Generic2ReportingMulti":
      case "Avalara_Generic2ReportingExtrastat":
        log.debug("getDocumentType: Found Avalara Generic format: ", connectorName);
        return ReportingDocumentType.TYPE_1;
      case "Avalara_Generic2ReportingIntrastat":
        return ReportingDocumentType.INTRASTAT_DOCUMENT_TYPE;
      default:
          log.debug("getDocumentType: XML is not from an Avalara template checking if SAP. ConnectorName: %s ", connectorName);
          if (!_.isEmpty(jsonpath.query(this.errorDataJson, SAP_DOCUMENT_TYPE_RV_JSON_PATH))) {
           log.debug("getDocumentType: Found SAP attributes in the XML treating as SAP type.");
            return ReportingDocumentType.SAP_DOCUMENT_TYPE_RV;
          } else if (!_.isEmpty(jsonpath.query(this.errorDataJson, SAP_INTRASTAT_INTRASTAT_RETURN_PATH))){
            log.debug("getDocumentType: Found SAP attributes in the XML treating as SAP Intrastat type.");
            return ReportingDocumentType.SAP_DOCUMENT_TYPE_RV;
          } else if (jsonpath.query(this.errorDataJson, MANUAL_CORRECTION_INVOICE_NUMBER_PATH)[0].includes('ManualCorrection')) {
            log.debug("getDocumentType: Found ManualCorrection in InvoiceNumber using template format.");
            return ReportingDocumentType.TYPE_1;
          } else {
            log.debug('getDocumentType: The XML format has not been recognised as SAP or Generic Template - unable to display it.');
            // return ReportingDocumentType.UNKNOWN;  SVR-6375 Default to Generic
              return ReportingDocumentType.TYPE_1;
          }
    }
  }

  getErrorData() {
    return JSON.parse(JSON.stringify(Object.assign({id: this.id}, this.errorData)));
  }

  transform(featureFlags = []) {
    switch (this.getDocumentType()) {
      case ReportingDocumentType.TYPE_1:
        var transformedJSON =  commonJSONTransformer(this.errorData, this.errorDataJson, ReportingDocumentType.TYPE_1, reportingDocumentErrorTemplate);
        //Added this block to handle the side-effect of seeing odd behaviour when we reprocess the transaction for example adding additional transaction date which totally change the PERCLOSED error to something else 
        //caused by passing empty OriginalTransactionDate and CorrectionDocumentType to Json transformer - DIYVR-447
        if(transformedJSON.errorJson.ReportingDocument.TransactionDate.$.OriginalTransactionDate === '' && transformedJSON.errorJson.ReportingDocument.TransactionDate.$.CorrectionDocumentType === '')
        {
          delete transformedJSON.errorJson.ReportingDocument.TransactionDate.$.OriginalTransactionDate;
          delete transformedJSON.errorJson.ReportingDocument.TransactionDate.$.CorrectionDocumentType;
        }
        return transformedJSON;
      case ReportingDocumentType.SAP_DOCUMENT_TYPE_RV:
        return commonJSONTransformer(this.errorData, this.errorDataJson, ReportingDocumentType.SAP_DOCUMENT_TYPE_RV, reportingDocumentSAPTypeRVTemplate);
      case ReportingDocumentType.INTRASTAT_DOCUMENT_TYPE:
        const useV2TemplateFlag = featureFlags[FeatureFlagEnum.USE_V2_INTRASTAT_ERRORDETAIL_TEMPLATE];
        const intrastatTemplate = useV2TemplateFlag ? reportingDocumentErrorIntrastatTemplateV2 : reportingDocumentErrorIntrastatTemplate;
        return commonJSONTransformer(this.errorData, this.errorDataJson, ReportingDocumentType.INTRASTAT_DOCUMENT_TYPE, intrastatTemplate);
      //No transformation just return as is
      default:
        return this.getErrorData();
   }
  }
}

export default ErrorDocumentUtil;
